// UltraFlex Admin - CRM Hub (Pipeline, Leads, Contacts, Drop-offs, Tasks, Automations)
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import {
  LayoutDashboard, Users, PhoneCall, Zap, AlertTriangle, Plus, Search,
  ChevronRight, Clock, Mail, Phone, Calendar, Check, X,
  MessageSquare, User, ArrowRight, Building2, Target, RefreshCw,
  Pin, PinOff, Trash2, Edit3, StickyNote, UserCheck, ChevronDown,
  FileText, Hash, MapPin, CreditCard, Shield, Star, GripVertical,
  ArrowUpRight, TrendingUp, Filter, Bell, Play, Pause, Settings2,
  Inbox, Tag, UserPlus, TimerOff, Award, Repeat, Send, Ticket
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CRMHub = () => {
  const { token, user } = useAuth();
  const [activeTab, setActiveTab] = useState('pipeline');
  const [sites, setSites] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [selectedSite, setSelectedSite] = useState('');
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  useEffect(() => {
    fetch(`${API_URL}/api/sites`, { headers }).then(r => r.json()).then(d => setSites(d.items || [])).catch(() => {});
    fetch(`${API_URL}/api/staff?page_size=100`, { headers }).then(r => r.json()).then(d => setStaffList(d.items || [])).catch(() => {});
  }, [token]);

  const tabs = [
    { id: 'pipeline', label: 'Pipeline', icon: TrendingUp },
    { id: 'contacts', label: 'Contacts', icon: User },
    { id: 'leads', label: 'All Leads', icon: Target },
    { id: 'dropoffs', label: 'Drop-Offs', icon: AlertTriangle },
    { id: 'tasks', label: 'Tasks & Calls', icon: PhoneCall },
    { id: 'automations', label: 'Automations', icon: Zap },
  ];

  return (
    <div className="p-4 lg:p-8" data-testid="crm-hub">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-uf-text" data-testid="crm-title">Lead Gen</h1>
          <p className="text-uf-text-muted text-sm mt-1">Pipeline, leads, tasks & automated journeys</p>
        </div>
        <select data-testid="crm-site-filter" value={selectedSite} onChange={e => setSelectedSite(e.target.value)}
          style={{ width: 'auto', minWidth: '160px' }}
          className="bg-uf-surface border border-uf-border rounded-lg px-3 py-2 text-uf-text text-sm">
          <option value="">All Sites</option>
          {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      </div>
      <div className="flex gap-1 mb-6 bg-uf-surface rounded-lg p-1 overflow-x-auto" data-testid="crm-tabs">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)} data-testid={`crm-tab-${t.id}`}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${activeTab === t.id ? 'bg-uf-red text-white' : 'text-uf-text-muted hover:text-uf-text'}`}>
            <t.icon className="w-4 h-4" />{t.label}
          </button>
        ))}
      </div>
      {activeTab === 'pipeline' && <PipelineView headers={headers} siteId={selectedSite} sites={sites} userId={user?.id} staffList={staffList} />}
      {activeTab === 'contacts' && <ContactsPanel headers={headers} siteId={selectedSite} sites={sites} userId={user?.id} />}
      {activeTab === 'leads' && <LeadsPanel headers={headers} siteId={selectedSite} sites={sites} userId={user?.id} />}
      {activeTab === 'dropoffs' && <DropOffsPanel headers={headers} siteId={selectedSite} />}
      {activeTab === 'tasks' && <TasksPanel headers={headers} siteId={selectedSite} sites={sites} userId={user?.id} />}
      {activeTab === 'automations' && <AutomationsPanel headers={headers} />}
    </div>
  );
};

// ==================== PIPELINE VIEW (KANBAN) ====================
const PIPELINE_STAGES = [
  { id: 'new', label: 'New', color: 'bg-blue-500', textColor: 'text-blue-400', bgTint: 'bg-blue-500/10', borderColor: 'border-blue-500/30' },
  { id: 'contacted', label: 'Contacted', color: 'bg-cyan-500', textColor: 'text-cyan-400', bgTint: 'bg-cyan-500/10', borderColor: 'border-cyan-500/30' },
  { id: 'tour_booked', label: 'Tour Booked', color: 'bg-indigo-500', textColor: 'text-indigo-400', bgTint: 'bg-indigo-500/10', borderColor: 'border-indigo-500/30' },
  { id: 'tour_done', label: 'Tour Done', color: 'bg-violet-500', textColor: 'text-violet-400', bgTint: 'bg-violet-500/10', borderColor: 'border-violet-500/30' },
  { id: 'negotiation', label: 'Negotiation', color: 'bg-amber-500', textColor: 'text-amber-400', bgTint: 'bg-amber-500/10', borderColor: 'border-amber-500/30' },
  { id: 'joined', label: 'Joined', color: 'bg-emerald-500', textColor: 'text-emerald-400', bgTint: 'bg-emerald-500/10', borderColor: 'border-emerald-500/30' },
  { id: 'lost', label: 'Lost', color: 'bg-red-500', textColor: 'text-red-400', bgTint: 'bg-red-500/10', borderColor: 'border-red-500/30' },
];

const PipelineView = ({ headers, siteId, sites, userId, staffList }) => {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [selectedStageFilter, setSelectedStageFilter] = useState('');
  const [newLead, setNewLead] = useState({ first_name: '', last_name: '', email: '', phone: '', source: 'manual', site_id: '' });

  const fetchLeads = useCallback(async () => {
    const p = new URLSearchParams();
    if (siteId) p.append('site_id', siteId);
    const res = await fetch(`${API_URL}/api/crm/leads?${p}`, { headers });
    const d = await res.json();
    setLeads(d.items || []);
  }, [siteId]);

  const fetchStats = useCallback(async () => {
    const p = siteId ? `?site_id=${siteId}` : '';
    const res = await fetch(`${API_URL}/api/crm/dashboard${p}`, { headers });
    if (res.ok) setStats(await res.json());
  }, [siteId]);

  useEffect(() => { fetchLeads(); fetchStats(); }, [fetchLeads, fetchStats]);

  const updateStatus = async (id, status) => {
    await fetch(`${API_URL}/api/crm/leads/${id}`, { method: 'PATCH', headers, body: JSON.stringify({ status }) });
    fetchLeads();
    fetchStats();
    if (selectedLead?.id === id) {
      const res = await fetch(`${API_URL}/api/crm/contacts/lead/${id}`, { headers });
      if (res.ok) setSelectedLead(await res.json());
    }
  };

  const createLead = async () => {
    await fetch(`${API_URL}/api/crm/leads`, { method: 'POST', headers, body: JSON.stringify(newLead) });
    setShowAdd(false);
    setNewLead({ first_name: '', last_name: '', email: '', phone: '', source: 'manual', site_id: '' });
    fetchLeads();
    fetchStats();
  };

  const openLead = async (id) => {
    const res = await fetch(`${API_URL}/api/crm/contacts/lead/${id}`, { headers });
    if (res.ok) setSelectedLead(await res.json());
  };

  // Group leads by stage
  const leadsByStage = {};
  PIPELINE_STAGES.forEach(s => { leadsByStage[s.id] = []; });
  leads.forEach(l => {
    if (leadsByStage[l.status]) leadsByStage[l.status].push(l);
    else if (leadsByStage['new']) leadsByStage['new'].push(l);
  });

  const totalLeads = leads.length;
  const activeLeads = leads.filter(l => !['joined', 'lost'].includes(l.status)).length;

  // Stat cards — drillable
  const statCards = [
    { label: 'Active Leads', value: activeLeads, color: 'text-blue-400', bg: 'bg-blue-500/10', stage: '' },
    { label: 'New', value: leadsByStage.new?.length || 0, color: 'text-blue-400', bg: 'bg-blue-500/10', stage: 'new' },
    { label: 'Touring', value: (leadsByStage.tour_booked?.length || 0) + (leadsByStage.tour_done?.length || 0), color: 'text-indigo-400', bg: 'bg-indigo-500/10', stage: 'tour_booked' },
    { label: 'Negotiation', value: leadsByStage.negotiation?.length || 0, color: 'text-amber-400', bg: 'bg-amber-500/10', stage: 'negotiation' },
    { label: 'Converted', value: leadsByStage.joined?.length || 0, color: 'text-emerald-400', bg: 'bg-emerald-500/10', stage: 'joined' },
    { label: 'Lost', value: leadsByStage.lost?.length || 0, color: 'text-red-400', bg: 'bg-red-500/10', stage: 'lost' },
  ];

  const filteredStages = selectedStageFilter
    ? PIPELINE_STAGES.filter(s => s.id === selectedStageFilter)
    : PIPELINE_STAGES.filter(s => !['joined', 'lost'].includes(s.id));

  const onDragEnd = (result) => {
    const { source, destination, draggableId } = result;
    if (!destination) return;
    if (source.droppableId === destination.droppableId && source.index === destination.index) return;
    const newStage = destination.droppableId;
    // Optimistic UI update
    setLeads(prev => prev.map(l => l.id === draggableId ? { ...l, status: newStage } : l));
    updateStatus(draggableId, newStage);
  };

  const assignLead = async (leadId, staffId) => {
    await fetch(`${API_URL}/api/crm/leads/${leadId}`, { method: 'PATCH', headers, body: JSON.stringify({ assigned_to: staffId || null }) });
    fetchLeads();
    if (selectedLead?.id === leadId) {
      const res = await fetch(`${API_URL}/api/crm/contacts/lead/${leadId}`, { headers });
      if (res.ok) setSelectedLead(await res.json());
    }
  };

  return (
    <div data-testid="pipeline-view">
      {/* Drillable Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6" data-testid="pipeline-stats">
        {statCards.map(c => (
          <div
            key={c.label}
            onClick={() => setSelectedStageFilter(c.stage === selectedStageFilter ? '' : c.stage)}
            className={`${c.bg} rounded-xl p-3 cursor-pointer transition-all hover:ring-1 ring-current ${selectedStageFilter === c.stage ? 'ring-2 ring-current' : ''}`}
            data-testid={`stat-${c.label.toLowerCase().replace(/\s/g, '-')}`}
          >
            <p className="text-uf-text-muted text-[10px] uppercase tracking-wider font-medium">{c.label}</p>
            <p className={`text-2xl font-bold ${c.color} mt-1`}>{c.value}</p>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-2">
          {selectedStageFilter && (
            <button onClick={() => setSelectedStageFilter('')} className="flex items-center gap-1 bg-uf-surface border border-uf-border px-3 py-1.5 rounded-lg text-xs text-uf-text hover:bg-uf-surface-hi">
              <X className="w-3 h-3" />Clear filter
            </button>
          )}
          <span className="text-uf-text-muted text-xs">{totalLeads} total leads</span>
        </div>
        <button data-testid="add-lead-btn" onClick={() => setShowAdd(true)} className="flex items-center gap-2 bg-uf-red text-white px-4 py-2 rounded-lg text-sm hover:bg-uf-red/90">
          <Plus className="w-4 h-4" />New Lead
        </button>
      </div>

      {/* Kanban Columns with Drag & Drop */}
      <DragDropContext onDragEnd={onDragEnd}>
        <div className="flex gap-3 overflow-x-auto pb-4" data-testid="pipeline-kanban">
          {filteredStages.map(stage => (
            <Droppable key={stage.id} droppableId={stage.id}>
              {(provided, snapshot) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className={`min-w-[240px] flex-1 rounded-xl border ${stage.borderColor} bg-uf-surface overflow-hidden transition-all ${snapshot.isDraggingOver ? 'ring-2 ring-uf-red/50 bg-uf-surface-hi' : ''}`}
                  data-testid={`stage-${stage.id}`}
                >
                  {/* Stage Header */}
                  <div className={`${stage.bgTint} px-3 py-2.5 border-b ${stage.borderColor} flex items-center justify-between`}>
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${stage.color}`} />
                      <span className={`text-xs font-semibold ${stage.textColor} uppercase tracking-wider`}>{stage.label}</span>
                    </div>
                    <span className={`text-xs font-bold ${stage.textColor}`}>{leadsByStage[stage.id]?.length || 0}</span>
                  </div>
                  {/* Lead Cards */}
                  <div className="p-2 space-y-2 max-h-[500px] overflow-y-auto min-h-[60px]">
                    {(leadsByStage[stage.id] || []).map((lead, index) => (
                      <Draggable key={lead.id} draggableId={lead.id} index={index}>
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            onClick={() => openLead(lead.id)}
                            className={`bg-uf-bg rounded-lg border p-3 cursor-pointer group transition-all ${snapshot.isDragging ? 'border-uf-red shadow-lg shadow-uf-red/10 rotate-1' : 'border-uf-border/50 hover:border-uf-border'}`}
                            data-testid={`lead-card-${lead.id}`}
                          >
                            <div className="flex items-start justify-between mb-1.5">
                              <div className="flex items-center gap-1.5">
                                <div {...provided.dragHandleProps} className="text-uf-text-muted hover:text-uf-text cursor-grab active:cursor-grabbing">
                                  <GripVertical className="w-3.5 h-3.5" />
                                </div>
                                <p className="text-uf-text text-sm font-medium">{lead.first_name} {lead.last_name}</p>
                              </div>
                              <span className="text-uf-text-muted text-[10px] capitalize">{lead.source?.replace('_', ' ')}</span>
                            </div>
                            <p className="text-uf-text-muted text-xs truncate">{lead.email}</p>
                            {lead.phone && <p className="text-uf-text-muted text-xs">{lead.phone}</p>}
                            <div className="flex items-center justify-between mt-2">
                              <span className="text-uf-text-muted text-[10px]">{lead.site_name}</span>
                              <span className="text-uf-text-muted text-[10px]">{lead.created_at?.split('T')[0]}</span>
                            </div>
                            {/* Assigned Staff Badge */}
                            {lead.assigned_name && (
                              <div className="flex items-center gap-1 mt-1.5">
                                <div className="w-4 h-4 rounded-full bg-violet-500/20 flex items-center justify-center">
                                  <UserCheck className="w-2.5 h-2.5 text-violet-400" />
                                </div>
                                <span className="text-violet-400 text-[10px]">{lead.assigned_name}</span>
                              </div>
                            )}
                            {/* Quick advance buttons */}
                            <div className="flex gap-1 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
                              {getNextStages(stage.id).map(ns => (
                                <button
                                  key={ns.id}
                                  onClick={e => { e.stopPropagation(); updateStatus(lead.id, ns.id); }}
                                  className={`flex-1 py-1 rounded text-[10px] ${ns.bgTint} ${ns.textColor} hover:ring-1 ring-current`}
                                  title={`Move to ${ns.label}`}
                                >
                                  {ns.label}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                    {!(leadsByStage[stage.id] || []).length && (
                      <p className="text-uf-text-muted text-xs text-center py-4">No leads</p>
                    )}
                  </div>
                </div>
              )}
            </Droppable>
          ))}
        </div>
      </DragDropContext>

      {/* Joined & Lost collapsed sections */}
      {!selectedStageFilter && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
          <CollapsedStage stage={PIPELINE_STAGES.find(s => s.id === 'joined')} leads={leadsByStage.joined || []} onOpen={openLead} />
          <CollapsedStage stage={PIPELINE_STAGES.find(s => s.id === 'lost')} leads={leadsByStage.lost || []} onOpen={openLead} />
        </div>
      )}

      {/* Lead Detail Slide-out */}
      {selectedLead && (
        <LeadDetailPanel
          lead={selectedLead}
          headers={headers}
          onClose={() => setSelectedLead(null)}
          onStatusChange={(id, status) => updateStatus(id, status)}
          onRefresh={() => openLead(selectedLead.id)}
          staffList={staffList}
          onAssign={assignLead}
        />
      )}

      {/* Add Lead Modal */}
      {showAdd && (
        <Modal title="New Lead" onClose={() => setShowAdd(false)}>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <input data-testid="new-lead-first" placeholder="First Name" value={newLead.first_name} onChange={e => setNewLead({...newLead, first_name: e.target.value})} className="bg-uf-surface-hi border border-uf-border rounded-lg px-3 py-2 text-uf-text text-sm" />
              <input placeholder="Last Name" value={newLead.last_name} onChange={e => setNewLead({...newLead, last_name: e.target.value})} className="bg-uf-surface-hi border border-uf-border rounded-lg px-3 py-2 text-uf-text text-sm" />
            </div>
            <input placeholder="Email" value={newLead.email} onChange={e => setNewLead({...newLead, email: e.target.value})} className="w-full bg-uf-surface-hi border border-uf-border rounded-lg px-3 py-2 text-uf-text text-sm" />
            <input placeholder="Phone" value={newLead.phone} onChange={e => setNewLead({...newLead, phone: e.target.value})} className="w-full bg-uf-surface-hi border border-uf-border rounded-lg px-3 py-2 text-uf-text text-sm" />
            <div className="grid grid-cols-2 gap-3">
              <select value={newLead.source} onChange={e => setNewLead({...newLead, source: e.target.value})} style={{ width: '100%' }} className="bg-uf-surface-hi border border-uf-border rounded-lg px-3 py-2 text-uf-text text-sm">
                {['manual','walk_in','referral','phone','campaign','web_dropoff','day_pass','contact_form','tour_booking'].map(s => <option key={s} value={s}>{s.replace(/_/g,' ')}</option>)}
              </select>
              <select value={newLead.site_id} onChange={e => setNewLead({...newLead, site_id: e.target.value})} style={{ width: '100%' }} className="bg-uf-surface-hi border border-uf-border rounded-lg px-3 py-2 text-uf-text text-sm">
                <option value="">Select Site</option>
                {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div className="flex gap-3 pt-2">
              <button onClick={() => setShowAdd(false)} className="flex-1 border border-uf-border text-uf-text py-2 rounded-lg text-sm">Cancel</button>
              <button data-testid="save-lead-btn" onClick={createLead} className="flex-1 bg-uf-red text-white py-2 rounded-lg text-sm">Save</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

function getNextStages(currentStageId) {
  const order = ['new', 'contacted', 'tour_booked', 'tour_done', 'negotiation', 'joined', 'lost'];
  const idx = order.indexOf(currentStageId);
  const next = [];
  if (idx < order.length - 2) next.push(PIPELINE_STAGES.find(s => s.id === order[idx + 1]));
  if (currentStageId !== 'lost') next.push(PIPELINE_STAGES.find(s => s.id === 'lost'));
  return next.filter(Boolean);
}

const CollapsedStage = ({ stage, leads, onOpen }) => {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className={`rounded-xl border ${stage.borderColor} ${stage.bgTint} overflow-hidden`}>
      <button onClick={() => setExpanded(!expanded)} className="w-full flex items-center justify-between p-3">
        <div className="flex items-center gap-2">
          <div className={`w-2 h-2 rounded-full ${stage.color}`} />
          <span className={`text-xs font-semibold ${stage.textColor} uppercase tracking-wider`}>{stage.label}</span>
          <span className={`text-xs font-bold ${stage.textColor}`}>{leads.length}</span>
        </div>
        <ChevronDown className={`w-4 h-4 ${stage.textColor} transition-transform ${expanded ? 'rotate-180' : ''}`} />
      </button>
      {expanded && (
        <div className="px-3 pb-3 space-y-1">
          {leads.map(l => (
            <div key={l.id} onClick={() => onOpen(l.id)} className="flex items-center justify-between bg-uf-bg/50 rounded-lg px-3 py-2 cursor-pointer hover:bg-uf-bg">
              <div>
                <p className="text-uf-text text-xs font-medium">{l.first_name} {l.last_name}</p>
                <p className="text-uf-text-muted text-[10px]">{l.email}</p>
              </div>
              <span className="text-uf-text-muted text-[10px]">{l.created_at?.split('T')[0]}</span>
            </div>
          ))}
          {!leads.length && <p className="text-uf-text-muted text-xs text-center py-2">None</p>}
        </div>
      )}
    </div>
  );
};

// ==================== LEAD DETAIL PANEL (SLIDE-OUT) ====================
const LeadDetailPanel = ({ lead, headers, onClose, onStatusChange, onRefresh, staffList = [], onAssign }) => {
  const [activeSection, setActiveSection] = useState('details');
  const [showInteraction, setShowInteraction] = useState(false);
  const [showAddNote, setShowAddNote] = useState(false);
  const [noteContent, setNoteContent] = useState('');
  const [noteType, setNoteType] = useState('general');
  const [newInteraction, setNewInteraction] = useState({ interaction_type: 'call', direction: 'outbound', subject: '', content: '', outcome: '' });

  const addInteraction = async () => {
    await fetch(`${API_URL}/api/crm/leads/${lead.id}/interactions`, { method: 'POST', headers, body: JSON.stringify(newInteraction) });
    setShowInteraction(false);
    setNewInteraction({ interaction_type: 'call', direction: 'outbound', subject: '', content: '', outcome: '' });
    onRefresh();
  };

  const addNote = async () => {
    if (!noteContent.trim()) return;
    await fetch(`${API_URL}/api/crm/notes/lead/${lead.id}`, { method: 'POST', headers, body: JSON.stringify({ content: noteContent, note_type: noteType }) });
    setShowAddNote(false);
    setNoteContent('');
    setNoteType('general');
    onRefresh();
  };

  const statusColors = { new: 'bg-blue-500/20 text-blue-400', contacted: 'bg-cyan-500/20 text-cyan-400', tour_booked: 'bg-indigo-500/20 text-indigo-400', tour_done: 'bg-violet-500/20 text-violet-400', negotiation: 'bg-amber-500/20 text-amber-400', joined: 'bg-emerald-500/20 text-emerald-400', lost: 'bg-red-500/20 text-red-400' };
  const noteTypeColors = { general: 'bg-gray-500/20 text-gray-400', call: 'bg-blue-500/20 text-blue-400', meeting: 'bg-violet-500/20 text-violet-400', follow_up: 'bg-amber-500/20 text-amber-400', complaint: 'bg-red-500/20 text-red-400', feedback: 'bg-emerald-500/20 text-emerald-400' };

  // Parse notes field — contact form data is stored here
  const leadNotes = lead.notes_text || lead.lead_notes || '';
  const parsedInfo = {};
  if (typeof leadNotes === 'string' && leadNotes) {
    leadNotes.split('\n').forEach(line => {
      const [key, ...val] = line.split(':');
      if (key && val.length) parsedInfo[key.trim()] = val.join(':').trim();
    });
  }

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="lead-detail-panel">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-uf-surface border-l border-uf-border h-full overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-uf-surface z-10 p-5 border-b border-uf-border">
          <div className="flex justify-between items-start mb-3">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-blue-500/20 flex items-center justify-center">
                <span className="text-lg font-bold text-blue-400">{lead.first_name?.[0]}{lead.last_name?.[0]}</span>
              </div>
              <div>
                <h3 className="text-lg font-bold text-uf-text" data-testid="lead-detail-name">{lead.first_name} {lead.last_name}</h3>
                <div className="flex items-center gap-2 text-xs text-uf-text-muted">
                  {lead.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{lead.email}</span>}
                  {lead.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{lead.phone}</span>}
                </div>
              </div>
            </div>
            <button onClick={onClose} className="text-uf-text-muted hover:text-uf-text p-1"><X className="w-5 h-5" /></button>
          </div>

          {/* Pipeline Status Buttons */}
          <div className="flex gap-1 flex-wrap mb-3" data-testid="lead-status-pipeline">
            {PIPELINE_STAGES.map(s => (
              <button key={s.id} onClick={() => onStatusChange(lead.id, s.id)}
                className={`px-2.5 py-1 rounded text-xs capitalize transition-all ${lead.status === s.id ? statusColors[s.id] + ' ring-1 ring-current font-medium' : 'bg-uf-surface-hi text-uf-text-muted hover:text-uf-text'}`}>
                {s.label}
              </button>
            ))}
          </div>

          {/* Quick Actions */}
          <div className="flex gap-2 flex-wrap">
            <button onClick={() => { setShowInteraction(true); setActiveSection('interactions'); }} className="flex items-center gap-1.5 bg-blue-500/10 text-blue-400 px-3 py-1.5 rounded-lg text-xs hover:bg-blue-500/20">
              <PhoneCall className="w-3 h-3" />Log Interaction
            </button>
            <button onClick={() => { setShowAddNote(true); setActiveSection('notes'); }} className="flex items-center gap-1.5 bg-amber-500/10 text-amber-400 px-3 py-1.5 rounded-lg text-xs hover:bg-amber-500/20">
              <StickyNote className="w-3 h-3" />Add Note
            </button>
          </div>

          {/* Staff Assignment */}
          <div className="flex items-center gap-2 mt-3" data-testid="lead-assign-staff">
            <UserCheck className="w-4 h-4 text-violet-400 flex-shrink-0" />
            <span className="text-uf-text-muted text-xs font-medium">Assign:</span>
            <select
              value={lead.assigned_to || ''}
              onChange={e => onAssign && onAssign(lead.id, e.target.value)}
              style={{ width: 'auto', minWidth: '160px' }}
              className="bg-uf-surface-hi border border-uf-border rounded-lg px-2 py-1.5 text-uf-text text-xs"
              data-testid="lead-assign-select"
            >
              <option value="">Unassigned</option>
              {staffList.map(s => (
                <option key={s.id} value={s.id}>{s.first_name} {s.last_name}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Section Tabs */}
        <div className="flex border-b border-uf-border px-2 sticky top-[180px] bg-uf-surface z-10">
          {[
            { id: 'details', label: 'Details' },
            { id: 'notes', label: `Notes (${(lead.notes || []).filter(n => typeof n === 'object').length})` },
            { id: 'interactions', label: `History (${(lead.interactions || []).length})` },
            { id: 'tasks', label: `Tasks (${(lead.tasks || []).length})` },
          ].map(s => (
            <button key={s.id} onClick={() => setActiveSection(s.id)}
              className={`px-4 py-2.5 text-xs font-medium border-b-2 transition-colors ${activeSection === s.id ? 'border-uf-red text-uf-red' : 'border-transparent text-uf-text-muted hover:text-uf-text'}`}>
              {s.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="p-5">
          {activeSection === 'details' && (
            <div className="space-y-4" data-testid="lead-details">
              <DetailRow icon={Target} label="Source" value={lead.source?.replace(/_/g, ' ')} />
              <DetailRow icon={MapPin} label="Site" value={lead.site_name} />
              <DetailRow icon={User} label="Assigned To" value={lead.assigned_name} />
              <DetailRow icon={Clock} label="Last Contact" value={lead.last_contact_at?.split('T')[0]} />
              <DetailRow icon={Calendar} label="Next Follow-Up" value={lead.next_follow_up?.split('T')[0]} />
              <DetailRow icon={Calendar} label="Created" value={lead.created_at?.split('T')[0]} />

              {/* Contact Form / Tour Info */}
              {Object.keys(parsedInfo).length > 0 && (
                <div className="bg-uf-surface-hi rounded-lg p-3 border border-uf-border" data-testid="lead-form-data">
                  <p className="text-xs text-uf-text-muted font-medium mb-2 uppercase tracking-wider flex items-center gap-1.5">
                    <Inbox className="w-3 h-3" />Incoming Form Data
                  </p>
                  <div className="space-y-1.5">
                    {Object.entries(parsedInfo).map(([k, v]) => (
                      <div key={k} className="flex gap-2 text-xs">
                        <span className="text-uf-text-muted font-medium min-w-[80px]">{k}:</span>
                        <span className="text-uf-text">{v}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Raw notes if no parsed info */}
              {leadNotes && Object.keys(parsedInfo).length === 0 && (
                <div className="bg-uf-surface-hi rounded-lg p-3 border border-uf-border">
                  <p className="text-xs text-uf-text-muted font-medium mb-1">Lead Notes</p>
                  <p className="text-uf-text text-sm whitespace-pre-wrap">{leadNotes}</p>
                </div>
              )}
            </div>
          )}

          {activeSection === 'notes' && (
            <div data-testid="lead-notes-section">
              {showAddNote && (
                <div className="mb-4 bg-uf-surface-hi rounded-lg border border-uf-border p-3 space-y-2">
                  <select value={noteType} onChange={e => setNoteType(e.target.value)} style={{ width: '100%' }}
                    className="bg-uf-surface border border-uf-border rounded px-2 py-1.5 text-uf-text text-xs">
                    {['general','call','meeting','follow_up','complaint','feedback'].map(t => <option key={t} value={t}>{t.replace('_',' ')}</option>)}
                  </select>
                  <textarea value={noteContent} onChange={e => setNoteContent(e.target.value)} placeholder="Write a note..." rows={3}
                    className="w-full bg-uf-surface border border-uf-border rounded-lg px-3 py-2 text-uf-text text-sm resize-none" />
                  <div className="flex gap-2 justify-end">
                    <button onClick={() => { setShowAddNote(false); setNoteContent(''); }} className="px-3 py-1.5 text-uf-text-muted text-xs">Cancel</button>
                    <button onClick={addNote} className="px-3 py-1.5 bg-uf-red text-white rounded-lg text-xs">Save Note</button>
                  </div>
                </div>
              )}
              <div className="space-y-2">
                {(lead.notes || []).filter(n => typeof n === 'object').map(note => (
                  <div key={note.id} className={`rounded-lg border p-3 ${note.is_pinned ? 'border-amber-500/30 bg-amber-500/5' : 'border-uf-border bg-uf-surface-hi'}`}>
                    <div className="flex items-center gap-2 text-[10px] text-uf-text-muted mb-1">
                      <span className={`px-1 py-0.5 rounded capitalize ${noteTypeColors[note.note_type] || noteTypeColors.general}`}>{note.note_type?.replace('_',' ')}</span>
                      {note.is_pinned && <Pin className="w-2.5 h-2.5 text-amber-400" />}
                      <span>{note.created_at?.replace('T',' ').slice(0,16)}</span>
                      {note.staff_name && <span>by {note.staff_name}</span>}
                    </div>
                    <p className="text-uf-text text-sm whitespace-pre-wrap">{note.content}</p>
                  </div>
                ))}
                {!(lead.notes || []).filter(n => typeof n === 'object').length && !showAddNote && (
                  <p className="text-uf-text-muted text-xs text-center py-6">No notes yet</p>
                )}
              </div>
            </div>
          )}

          {activeSection === 'interactions' && (
            <div className="space-y-3" data-testid="lead-interactions">
              {showInteraction && (
                <div className="mb-4 bg-uf-surface-hi rounded-lg border border-uf-border p-3 space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <select value={newInteraction.interaction_type} onChange={e => setNewInteraction({...newInteraction, interaction_type: e.target.value})} style={{ width: '100%' }} className="bg-uf-surface border border-uf-border rounded-lg px-2 py-1.5 text-uf-text text-sm">
                      {['call','email','sms','note','tour','walk_in'].map(t => <option key={t} value={t}>{t.replace('_',' ')}</option>)}
                    </select>
                    <select value={newInteraction.outcome} onChange={e => setNewInteraction({...newInteraction, outcome: e.target.value})} style={{ width: '100%' }} className="bg-uf-surface border border-uf-border rounded-lg px-2 py-1.5 text-uf-text text-sm">
                      <option value="">Outcome</option>
                      {['answered','no_answer','voicemail','callback','booked','declined'].map(o => <option key={o} value={o}>{o.replace('_',' ')}</option>)}
                    </select>
                  </div>
                  <input placeholder="Subject" value={newInteraction.subject} onChange={e => setNewInteraction({...newInteraction, subject: e.target.value})} className="w-full bg-uf-surface border border-uf-border rounded-lg px-3 py-2 text-uf-text text-sm" />
                  <textarea placeholder="Notes" value={newInteraction.content} onChange={e => setNewInteraction({...newInteraction, content: e.target.value})} className="w-full bg-uf-surface border border-uf-border rounded-lg px-3 py-2 text-uf-text text-sm h-20 resize-none" />
                  <div className="flex gap-2 justify-end">
                    <button onClick={() => setShowInteraction(false)} className="px-3 py-1.5 text-uf-text-muted text-xs">Cancel</button>
                    <button onClick={addInteraction} className="px-3 py-1.5 bg-uf-red text-white rounded-lg text-xs">Save</button>
                  </div>
                </div>
              )}
              {(lead.interactions || []).map(i => (
                <div key={i.id} className="border-l-2 border-uf-border pl-3 py-1">
                  <div className="flex items-center gap-2 text-xs">
                    {i.interaction_type === 'call' ? <Phone className="w-3 h-3 text-blue-400" /> : i.interaction_type === 'email' ? <Mail className="w-3 h-3 text-emerald-400" /> : <MessageSquare className="w-3 h-3 text-violet-400" />}
                    <span className="text-uf-text capitalize font-medium">{i.interaction_type} ({i.direction})</span>
                    {i.outcome && <span className="text-uf-text-muted">- {i.outcome}</span>}
                  </div>
                  {i.subject && <p className="text-uf-text text-xs font-medium mt-1">{i.subject}</p>}
                  {i.content && <p className="text-uf-text-muted text-xs mt-0.5">{i.content}</p>}
                  <p className="text-uf-text-muted text-[10px] mt-1">{i.created_at?.replace('T', ' ').slice(0, 16)} {i.staff_name && `by ${i.staff_name}`}</p>
                </div>
              ))}
              {!lead.interactions?.length && !showInteraction && <p className="text-uf-text-muted text-xs text-center py-6">No interactions recorded</p>}
            </div>
          )}

          {activeSection === 'tasks' && (
            <div className="space-y-2" data-testid="lead-tasks">
              {(lead.tasks || []).map(t => <TaskCard key={t.id} task={t} priorityColors={priorityColorsMap} typeIcons={typeIconsMap} />)}
              {!(lead.tasks || []).length && <p className="text-uf-text-muted text-xs text-center py-6">No tasks linked</p>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ==================== CONTACTS (UNIFIED) ====================
const ContactsPanel = ({ headers, siteId, sites, userId }) => {
  const [contacts, setContacts] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [selectedContact, setSelectedContact] = useState(null);

  const fetchContacts = useCallback(async () => {
    const p = new URLSearchParams();
    if (siteId) p.append('site_id', siteId);
    if (search) p.append('search', search);
    if (typeFilter) p.append('contact_type', typeFilter);
    const res = await fetch(`${API_URL}/api/crm/contacts?${p}`, { headers });
    const d = await res.json();
    setContacts(d.items || []);
    setTotal(d.total || 0);
  }, [siteId, search, typeFilter]);

  useEffect(() => { fetchContacts(); }, [fetchContacts]);

  const openCard = async (type, id) => {
    const res = await fetch(`${API_URL}/api/crm/contacts/${type}/${id}`, { headers });
    if (res.ok) setSelectedContact(await res.json());
  };

  return (
    <div data-testid="contacts-panel">
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="flex-1 relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-uf-text-muted" />
          <input data-testid="contact-search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search contacts..."
            style={{ background: 'var(--uf-surface)', border: '1px solid var(--uf-border)', borderRadius: '0.5rem', padding: '0.5rem 0.75rem 0.5rem 2.5rem' }}
            className="w-full text-uf-text text-sm placeholder:text-uf-text-muted" />
        </div>
        <select data-testid="contact-type-filter" value={typeFilter} onChange={e => setTypeFilter(e.target.value)}
          style={{ width: 'auto', minWidth: '140px' }}
          className="bg-uf-surface border border-uf-border rounded-lg px-3 py-2 text-uf-text text-sm">
          <option value="">All Types</option>
          <option value="member">Members</option>
          <option value="lead">Leads</option>
        </select>
      </div>

      <div className="flex gap-4">
        <div className={`${selectedContact ? 'w-5/12' : 'w-full'} transition-all`}>
          <div className="bg-uf-surface rounded-xl border border-uf-border overflow-hidden">
            <div className="px-4 py-3 border-b border-uf-border">
              <span className="text-uf-text-muted text-xs font-medium">{total} contacts</span>
            </div>
            <div className="divide-y divide-uf-border/50 max-h-[600px] overflow-y-auto">
              {contacts.map(c => (
                <div key={`${c.contact_type}-${c.id}`} onClick={() => openCard(c.contact_type, c.id)}
                  className={`flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-uf-surface-hi transition-colors ${selectedContact?.id === c.id ? 'bg-uf-surface-hi' : ''}`} data-testid={`contact-row-${c.id}`}>
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${c.contact_type === 'member' ? 'bg-emerald-500/20' : 'bg-blue-500/20'}`}>
                    <span className={`text-sm font-bold ${c.contact_type === 'member' ? 'text-emerald-400' : 'text-blue-400'}`}>{c.first_name?.[0]}{c.last_name?.[0]}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-uf-text font-medium text-sm truncate">{c.first_name} {c.last_name}</p>
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase ${c.contact_type === 'member' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-blue-500/20 text-blue-400'}`}>{c.contact_type}</span>
                    </div>
                    <p className="text-uf-text-muted text-xs truncate">{c.email}</p>
                  </div>
                  <span className="text-uf-text-muted text-[10px]">{c.site_name}</span>
                </div>
              ))}
              {!contacts.length && <div className="p-8 text-center text-uf-text-muted text-sm">No contacts found</div>}
            </div>
          </div>
        </div>
        {selectedContact && (
          <ContactCard contact={selectedContact} headers={headers} onClose={() => setSelectedContact(null)} onRefresh={() => openCard(selectedContact.contact_type, selectedContact.id)} />
        )}
      </div>
    </div>
  );
};

// ==================== CONTACT CARD ====================
const ContactCard = ({ contact, headers, onClose, onRefresh }) => {
  const [activeSection, setActiveSection] = useState('details');
  const [showAddNote, setShowAddNote] = useState(false);
  const [showInteraction, setShowInteraction] = useState(false);
  const [noteContent, setNoteContent] = useState('');
  const [noteType, setNoteType] = useState('general');
  const [newInteraction, setNewInteraction] = useState({ interaction_type: 'call', direction: 'outbound', subject: '', content: '', outcome: '' });

  const addNote = async () => {
    if (!noteContent.trim()) return;
    await fetch(`${API_URL}/api/crm/notes/${contact.contact_type}/${contact.id}`, {
      method: 'POST', headers, body: JSON.stringify({ content: noteContent, note_type: noteType })
    });
    setNoteContent(''); setNoteType('general'); setShowAddNote(false); onRefresh();
  };

  const addInteraction = async () => {
    if (contact.contact_type === 'lead') {
      await fetch(`${API_URL}/api/crm/leads/${contact.id}/interactions`, {
        method: 'POST', headers, body: JSON.stringify(newInteraction)
      });
    } else {
      // For members, log as a note with interaction type
      await fetch(`${API_URL}/api/crm/notes/${contact.contact_type}/${contact.id}`, {
        method: 'POST', headers, body: JSON.stringify({
          content: `${newInteraction.interaction_type} (${newInteraction.direction}): ${newInteraction.subject}\n${newInteraction.content}${newInteraction.outcome ? `\nOutcome: ${newInteraction.outcome}` : ''}`,
          note_type: newInteraction.interaction_type === 'call' ? 'call' : newInteraction.interaction_type === 'meeting' ? 'meeting' : 'general'
        })
      });
    }
    setShowInteraction(false);
    setNewInteraction({ interaction_type: 'call', direction: 'outbound', subject: '', content: '', outcome: '' });
    onRefresh();
  };

  const togglePin = async (noteId, isPinned) => {
    await fetch(`${API_URL}/api/crm/notes/${noteId}`, { method: 'PATCH', headers, body: JSON.stringify({ is_pinned: !isPinned }) });
    onRefresh();
  };

  const deleteNote = async (noteId) => {
    await fetch(`${API_URL}/api/crm/notes/${noteId}`, { method: 'DELETE', headers }); onRefresh();
  };

  const isMember = contact.contact_type === 'member';
  const statusColors = {
    active: 'bg-emerald-500/20 text-emerald-400', pending: 'bg-amber-500/20 text-amber-400',
    new: 'bg-blue-500/20 text-blue-400', contacted: 'bg-cyan-500/20 text-cyan-400',
    joined: 'bg-emerald-500/20 text-emerald-400', lost: 'bg-red-500/20 text-red-400',
  };
  const noteTypeColors = { general: 'bg-gray-500/20 text-gray-400', call: 'bg-blue-500/20 text-blue-400', meeting: 'bg-violet-500/20 text-violet-400', follow_up: 'bg-amber-500/20 text-amber-400' };

  return (
    <div className="w-7/12 bg-uf-surface rounded-xl border border-uf-border overflow-hidden" data-testid="contact-card">
      <div className="p-5 border-b border-uf-border">
        <div className="flex justify-between items-start">
          <div className="flex items-center gap-4">
            <div className={`w-14 h-14 rounded-full flex items-center justify-center ${isMember ? 'bg-emerald-500/20 ring-2 ring-emerald-500/30' : 'bg-blue-500/20 ring-2 ring-blue-500/30'}`}>
              <span className={`text-xl font-bold ${isMember ? 'text-emerald-400' : 'text-blue-400'}`}>{contact.first_name?.[0]}{contact.last_name?.[0]}</span>
            </div>
            <div>
              <h3 className="text-lg font-bold text-uf-text" data-testid="contact-card-name">{contact.first_name} {contact.last_name}</h3>
              <div className="flex items-center gap-2 mt-0.5">
                <span className={`px-2 py-0.5 rounded text-xs font-semibold uppercase ${isMember ? 'bg-emerald-500/20 text-emerald-400' : 'bg-blue-500/20 text-blue-400'}`}>{contact.contact_type}</span>
                <span className={`px-2 py-0.5 rounded text-xs capitalize ${statusColors[contact.status] || 'bg-gray-500/20 text-gray-400'}`}>{contact.status?.replace('_', ' ')}</span>
              </div>
            </div>
          </div>
          <button onClick={onClose} className="text-uf-text-muted hover:text-uf-text p-1"><X className="w-5 h-5" /></button>
        </div>
        <div className="flex flex-wrap gap-4 mt-4 text-xs">
          {contact.email && <span className="flex items-center gap-1.5 text-uf-text-muted"><Mail className="w-3.5 h-3.5 text-uf-red" />{contact.email}</span>}
          {contact.phone && <span className="flex items-center gap-1.5 text-uf-text-muted"><Phone className="w-3.5 h-3.5 text-uf-red" />{contact.phone}</span>}
          {contact.site_name && <span className="flex items-center gap-1.5 text-uf-text-muted"><MapPin className="w-3.5 h-3.5 text-uf-red" />{contact.site_name}</span>}
        </div>
        <div className="flex gap-2 mt-3">
          <button onClick={() => { setShowInteraction(true); setActiveSection('interactions'); }} className="flex items-center gap-1.5 bg-blue-500/10 text-blue-400 px-3 py-1.5 rounded-lg text-xs hover:bg-blue-500/20" data-testid="contact-add-interaction">
            <PhoneCall className="w-3 h-3" />Log Interaction
          </button>
          <button onClick={() => { setShowAddNote(true); setActiveSection('notes'); }} className="flex items-center gap-1.5 bg-amber-500/10 text-amber-400 px-3 py-1.5 rounded-lg text-xs hover:bg-amber-500/20" data-testid="contact-add-note">
            <StickyNote className="w-3 h-3" />Add Note
          </button>
        </div>
      </div>

      <div className="flex border-b border-uf-border px-2">
        {[{ id: 'details', label: 'Details' }, { id: 'interactions', label: 'Interactions' }, { id: 'notes', label: `Notes (${(contact.notes || []).filter(n => typeof n === 'object').length})` }].map(s => (
          <button key={s.id} onClick={() => setActiveSection(s.id)}
            className={`px-4 py-2.5 text-xs font-medium border-b-2 transition-colors ${activeSection === s.id ? 'border-uf-red text-uf-red' : 'border-transparent text-uf-text-muted hover:text-uf-text'}`}>{s.label}</button>
        ))}
      </div>

      <div className="p-5 overflow-y-auto max-h-[400px]">
        {activeSection === 'interactions' && (
          <div data-testid="contact-interactions-section">
            <div className="mb-4 bg-uf-surface-hi rounded-lg border border-uf-border p-3 space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] text-uf-text-muted uppercase">Type</label>
                  <select value={newInteraction.interaction_type} onChange={e => setNewInteraction({...newInteraction, interaction_type: e.target.value})}
                    className="w-full bg-uf-surface border border-uf-border rounded-lg px-2 py-1.5 text-uf-text text-sm">
                    <option value="call">Call</option><option value="email">Email</option><option value="meeting">Meeting</option><option value="sms">SMS</option>
                  </select>
                </div>
                <div>
                  <label className="text-[10px] text-uf-text-muted uppercase">Direction</label>
                  <select value={newInteraction.direction} onChange={e => setNewInteraction({...newInteraction, direction: e.target.value})}
                    className="w-full bg-uf-surface border border-uf-border rounded-lg px-2 py-1.5 text-uf-text text-sm">
                    <option value="outbound">Outbound</option><option value="inbound">Inbound</option>
                  </select>
                </div>
              </div>
              <input type="text" placeholder="Subject" value={newInteraction.subject} onChange={e => setNewInteraction({...newInteraction, subject: e.target.value})}
                className="w-full bg-uf-surface border border-uf-border rounded-lg px-3 py-2 text-uf-text text-sm" />
              <textarea placeholder="Details..." value={newInteraction.content} onChange={e => setNewInteraction({...newInteraction, content: e.target.value})} rows={2}
                className="w-full bg-uf-surface border border-uf-border rounded-lg px-3 py-2 text-uf-text text-sm resize-none" />
              <div className="flex gap-2 justify-end">
                <button onClick={() => setShowInteraction(false)} className="px-3 py-1.5 text-uf-text-muted text-xs">Cancel</button>
                <button onClick={addInteraction} className="px-3 py-1.5 bg-uf-red text-white rounded-lg text-xs" data-testid="contact-save-interaction">Save</button>
              </div>
            </div>
            {contact.interactions?.length > 0 ? contact.interactions.map((i, idx) => (
              <div key={idx} className="bg-uf-surface-hi rounded-lg border border-uf-border p-3 mb-2">
                <div className="flex items-center gap-2 text-xs text-uf-text-muted mb-1">
                  <span className="capitalize font-medium text-uf-text">{i.interaction_type} ({i.direction})</span>
                  <span>{i.created_at?.split('T')[0]}</span>
                </div>
                {i.subject && <p className="text-uf-text text-sm font-medium">{i.subject}</p>}
                {i.content && <p className="text-uf-text-muted text-xs mt-1">{i.content}</p>}
              </div>
            )) : (
              <p className="text-uf-text-muted text-xs text-center py-6">No interactions recorded</p>
            )}
          </div>
        )}
        {activeSection === 'details' && (
          <div className="space-y-4">
            {isMember && (
              <>
                <DetailRow icon={MapPin} label="Address" value={[contact.address_line1, contact.city, contact.postcode].filter(Boolean).join(', ')} />
                <DetailRow icon={Calendar} label="Date of Birth" value={contact.date_of_birth} />
                {contact.subscription && (
                  <div className="bg-uf-surface-hi rounded-lg p-3 border border-uf-border">
                    <p className="text-xs text-uf-text-muted font-medium mb-1 uppercase tracking-wider">Subscription</p>
                    <div className="flex items-center gap-3">
                      <CreditCard className="w-4 h-4 text-uf-red" />
                      <span className="text-uf-text text-sm">{contact.subscription.status} - £{contact.subscription.monthly_amount}/mo</span>
                    </div>
                  </div>
                )}
              </>
            )}
            {!isMember && (
              <>
                <DetailRow icon={Target} label="Source" value={contact.source?.replace(/_/g, ' ')} />
                <DetailRow icon={User} label="Assigned To" value={contact.assigned_name} />
                <DetailRow icon={Clock} label="Last Contact" value={contact.last_contact_at?.split('T')[0]} />
              </>
            )}
            <DetailRow icon={Calendar} label="Created" value={contact.created_at?.split('T')[0]} />
          </div>
        )}
        {activeSection === 'notes' && (
          <div>
            <div className="flex justify-end mb-3">
              <button onClick={() => setShowAddNote(true)} className="flex items-center gap-1.5 bg-uf-red/10 text-uf-red px-3 py-1.5 rounded-lg text-xs hover:bg-uf-red/20"><Plus className="w-3 h-3" />Add Note</button>
            </div>
            {showAddNote && (
              <div className="mb-4 bg-uf-surface-hi rounded-lg border border-uf-border p-3 space-y-2">
                <select value={noteType} onChange={e => setNoteType(e.target.value)} style={{ width: '100%' }}
                  className="bg-uf-surface border border-uf-border rounded px-2 py-1.5 text-uf-text text-xs">
                  {['general','call','meeting','follow_up','complaint','feedback'].map(t => <option key={t} value={t}>{t.replace('_',' ')}</option>)}
                </select>
                <textarea value={noteContent} onChange={e => setNoteContent(e.target.value)} placeholder="Write a note..." rows={3}
                  className="w-full bg-uf-surface border border-uf-border rounded-lg px-3 py-2 text-uf-text text-sm resize-none" />
                <div className="flex gap-2 justify-end">
                  <button onClick={() => { setShowAddNote(false); setNoteContent(''); }} className="px-3 py-1.5 text-uf-text-muted text-xs">Cancel</button>
                  <button onClick={addNote} className="px-3 py-1.5 bg-uf-red text-white rounded-lg text-xs">Save</button>
                </div>
              </div>
            )}
            <div className="space-y-2">
              {(contact.notes || []).filter(n => typeof n === 'object').map(note => (
                <div key={note.id} className={`rounded-lg border p-3 ${note.is_pinned ? 'border-amber-500/30 bg-amber-500/5' : 'border-uf-border bg-uf-surface-hi'}`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2 text-[10px] text-uf-text-muted mb-1">
                        <span className={`px-1 py-0.5 rounded capitalize ${noteTypeColors[note.note_type] || noteTypeColors.general}`}>{note.note_type?.replace('_',' ')}</span>
                        <span>{note.created_at?.replace('T',' ').slice(0,16)}</span>
                        {note.staff_name && <span>by {note.staff_name}</span>}
                      </div>
                      <p className="text-uf-text text-sm whitespace-pre-wrap">{note.content}</p>
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      <button onClick={() => togglePin(note.id, note.is_pinned)} className="p-1 text-uf-text-muted hover:text-amber-400">
                        {note.is_pinned ? <PinOff className="w-3.5 h-3.5" /> : <Pin className="w-3.5 h-3.5" />}
                      </button>
                      <button onClick={() => deleteNote(note.id)} className="p-1 text-uf-text-muted hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </div>
                </div>
              ))}
              {!(contact.notes || []).filter(n => typeof n === 'object').length && !showAddNote && <p className="text-uf-text-muted text-xs text-center py-6">No notes yet</p>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const DetailRow = ({ icon: Icon, label, value }) => {
  if (!value) return null;
  return (
    <div className="flex items-start gap-3">
      <Icon className="w-4 h-4 text-uf-text-muted mt-0.5 flex-shrink-0" />
      <div>
        <p className="text-uf-text-muted text-[10px] uppercase tracking-wider font-medium">{label}</p>
        <p className="text-uf-text text-sm capitalize">{value}</p>
      </div>
    </div>
  );
};

const priorityColorsMap = { urgent: 'text-red-400', high: 'text-orange-400', medium: 'text-amber-400', low: 'text-emerald-400' };
const typeIconsMap = { call: Phone, email: Mail, follow_up: RefreshCw, tour: Building2, general: Target };

// ==================== LEADS (ALL - TABLE VIEW) ====================
const LeadsPanel = ({ headers, siteId, sites, userId }) => {
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const fetchLeads = useCallback(async () => {
    const p = new URLSearchParams();
    if (siteId) p.append('site_id', siteId);
    if (search) p.append('search', search);
    if (statusFilter) p.append('status', statusFilter);
    const res = await fetch(`${API_URL}/api/crm/leads?${p}`, { headers });
    const d = await res.json();
    setLeads(d.items || []);
    setTotal(d.total || 0);
  }, [siteId, search, statusFilter]);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  const statusColors = { new: 'bg-blue-500/20 text-blue-400', contacted: 'bg-cyan-500/20 text-cyan-400', tour_booked: 'bg-indigo-500/20 text-indigo-400', tour_done: 'bg-violet-500/20 text-violet-400', negotiation: 'bg-amber-500/20 text-amber-400', joined: 'bg-emerald-500/20 text-emerald-400', lost: 'bg-red-500/20 text-red-400' };

  return (
    <div data-testid="leads-panel">
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="flex-1 relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-uf-text-muted" />
          <input data-testid="lead-search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search leads..."
            style={{ background: 'var(--uf-surface)', border: '1px solid var(--uf-border)', borderRadius: '0.5rem', padding: '0.5rem 0.75rem 0.5rem 2.5rem' }}
            className="w-full text-uf-text text-sm placeholder:text-uf-text-muted" />
        </div>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          style={{ width: 'auto', minWidth: '150px' }}
          className="bg-uf-surface border border-uf-border rounded-lg px-3 py-2 text-uf-text text-sm">
          <option value="">All Statuses</option>
          {['new','contacted','tour_booked','tour_done','negotiation','joined','lost'].map(s => <option key={s} value={s}>{s.replace('_',' ')}</option>)}
        </select>
      </div>
      <div className="bg-uf-surface rounded-xl border border-uf-border overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-uf-border text-left text-uf-text-muted">
            <th className="p-3">Name</th><th className="p-3">Email</th><th className="p-3">Source</th><th className="p-3">Status</th><th className="p-3">Site</th><th className="p-3">Created</th>
          </tr></thead>
          <tbody>
            {leads.map(l => (
              <tr key={l.id} className="border-b border-uf-border/50 hover:bg-uf-surface-hi" data-testid={`lead-row-${l.id}`}>
                <td className="p-3 text-uf-text font-medium">{l.first_name} {l.last_name}</td>
                <td className="p-3 text-uf-text-muted text-xs">{l.email}</td>
                <td className="p-3 text-uf-text-muted text-xs capitalize">{l.source?.replace(/_/g, ' ')}</td>
                <td className="p-3"><span className={`px-2 py-0.5 rounded text-xs capitalize ${statusColors[l.status] || 'bg-gray-500/20 text-gray-400'}`}>{l.status?.replace('_', ' ')}</span></td>
                <td className="p-3 text-uf-text-muted text-xs">{l.site_name}</td>
                <td className="p-3 text-uf-text-muted text-xs">{l.created_at?.split('T')[0]}</td>
              </tr>
            ))}
            {!leads.length && <tr><td colSpan={6} className="p-8 text-center text-uf-text-muted">No leads found</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ==================== DROP-OFFS ====================
const DropOffsPanel = ({ headers, siteId }) => {
  const [dropoffs, setDropoffs] = useState([]);
  const [filter, setFilter] = useState('unrecovered');

  const fetchDropoffs = useCallback(async () => {
    const p = new URLSearchParams();
    if (siteId) p.append('site_id', siteId);
    if (filter === 'unrecovered') p.append('recovered', 'false');
    else if (filter === 'recovered') p.append('recovered', 'true');
    const res = await fetch(`${API_URL}/api/crm/dropoffs?${p}`, { headers });
    const d = await res.json();
    setDropoffs(d.items || []);
  }, [siteId, filter]);

  useEffect(() => { fetchDropoffs(); }, [fetchDropoffs]);

  const convertToLead = async (id) => {
    await fetch(`${API_URL}/api/crm/dropoffs/${id}/convert-to-lead`, { method: 'POST', headers, body: '{}' });
    fetchDropoffs();
  };

  const stepLabels = { personal_details: 'Personal Details', plan_selection: 'Plan Selection', payment_details: 'Payment Details', bank_details: 'Bank Details' };
  const stepProgress = { personal_details: 25, plan_selection: 50, payment_details: 75, bank_details: 90 };

  return (
    <div data-testid="dropoffs-panel">
      <div className="flex gap-3 mb-4">
        {['unrecovered', 'recovered', 'all'].map(f => (
          <button key={f} onClick={() => setFilter(f)}
            className={`px-4 py-1.5 rounded-lg text-sm capitalize ${filter === f ? 'bg-uf-red text-white' : 'bg-uf-surface border border-uf-border text-uf-text-muted hover:text-uf-text'}`}>{f}</button>
        ))}
      </div>
      <div className="space-y-3">
        {dropoffs.map(d => (
          <div key={d.id} className="bg-uf-surface rounded-xl border border-uf-border p-4 flex items-center gap-4" data-testid={`dropoff-${d.id}`}>
            <div className="w-10 h-10 bg-amber-500/20 rounded-full flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="w-5 h-5 text-amber-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-uf-text font-medium text-sm">{d.first_name || 'Unknown'} {d.last_name || ''}</p>
              <p className="text-uf-text-muted text-xs truncate">{d.email || 'No email'}</p>
              <div className="flex items-center gap-3 mt-1">
                <span className="text-uf-text-muted text-xs">Dropped at: <span className="text-amber-400">{stepLabels[d.step_reached] || d.step_reached}</span></span>
                <div className="w-24 h-1.5 bg-uf-surface-hi rounded-full overflow-hidden">
                  <div className="h-full bg-amber-500 rounded-full" style={{ width: `${stepProgress[d.step_reached] || 10}%` }} />
                </div>
              </div>
            </div>
            <div className="text-right flex-shrink-0">
              <p className="text-uf-text-muted text-xs">{d.created_at?.split('T')[0]}</p>
              <p className="text-uf-text-muted text-xs">{d.site_name}</p>
            </div>
            {!d.recovered ? (
              <button onClick={() => convertToLead(d.id)} className="flex items-center gap-1 bg-uf-red/10 text-uf-red px-3 py-1.5 rounded-lg text-xs hover:bg-uf-red/20 flex-shrink-0">
                <ArrowRight className="w-3 h-3" />Convert
              </button>
            ) : (
              <span className="text-emerald-400 text-xs flex items-center gap-1 flex-shrink-0"><Check className="w-3 h-3" />Recovered</span>
            )}
          </div>
        ))}
        {!dropoffs.length && <div className="bg-uf-surface rounded-xl border border-uf-border p-8 text-center text-uf-text-muted">No drop-offs found</div>}
      </div>
    </div>
  );
};

// ==================== TASKS ====================
const TasksPanel = ({ headers, siteId, sites, userId }) => {
  const [tasks, setTasks] = useState([]);
  const [schedule, setSchedule] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [view, setView] = useState('schedule');
  const [newTask, setNewTask] = useState({ task_type: 'call', priority: 'medium', subject: '', description: '', due_date: new Date().toISOString().split('T')[0], due_time: '09:00', assigned_to: '', site_id: '' });

  const fetchSchedule = useCallback(async () => {
    const res = await fetch(`${API_URL}/api/crm/tasks/my-schedule`, { headers });
    setSchedule(await res.json());
  }, []);

  const fetchTasks = useCallback(async () => {
    const p = new URLSearchParams();
    if (siteId) p.append('site_id', siteId);
    const res = await fetch(`${API_URL}/api/crm/tasks?${p}`, { headers });
    const d = await res.json();
    setTasks(d.items || []);
  }, [siteId]);

  useEffect(() => { fetchSchedule(); fetchTasks(); }, [fetchSchedule, fetchTasks]);

  const createTask = async () => {
    const body = { ...newTask, assigned_to: newTask.assigned_to || userId };
    await fetch(`${API_URL}/api/crm/tasks`, { method: 'POST', headers, body: JSON.stringify(body) });
    setShowAdd(false);
    setNewTask({ task_type: 'call', priority: 'medium', subject: '', description: '', due_date: new Date().toISOString().split('T')[0], due_time: '09:00', assigned_to: '', site_id: '' });
    fetchSchedule(); fetchTasks();
  };

  const completeTask = async (id) => {
    await fetch(`${API_URL}/api/crm/tasks/${id}/complete`, { method: 'POST', headers, body: '{}' });
    fetchSchedule(); fetchTasks();
  };

  return (
    <div data-testid="tasks-panel">
      <div className="flex justify-between items-center mb-4">
        <div className="flex gap-2">
          {['schedule', 'all'].map(v => (
            <button key={v} onClick={() => setView(v)} className={`px-4 py-1.5 rounded-lg text-sm capitalize ${view === v ? 'bg-uf-red text-white' : 'bg-uf-surface border border-uf-border text-uf-text-muted'}`}>{v === 'schedule' ? 'My Day' : 'All Tasks'}</button>
          ))}
        </div>
        <button data-testid="add-task-btn" onClick={() => setShowAdd(true)} className="flex items-center gap-2 bg-uf-red text-white px-4 py-2 rounded-lg text-sm"><Plus className="w-4 h-4" />New Task</button>
      </div>

      {view === 'schedule' && schedule && (
        <div className="space-y-4">
          {schedule.overdue_count > 0 && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
              <h3 className="text-red-400 font-semibold text-sm mb-2">Overdue ({schedule.overdue_count})</h3>
              {schedule.overdue.map(t => <TaskCard key={t.id} task={t} onComplete={completeTask} priorityColors={priorityColorsMap} typeIcons={typeIconsMap} />)}
            </div>
          )}
          <div>
            <h3 className="text-uf-text font-semibold text-sm mb-2">Today - {schedule.date} ({schedule.total_today} tasks)</h3>
            {schedule.tasks.map(t => <TaskCard key={t.id} task={t} onComplete={completeTask} priorityColors={priorityColorsMap} typeIcons={typeIconsMap} />)}
            {!schedule.tasks.length && !schedule.overdue_count && <div className="bg-uf-surface rounded-xl border border-uf-border p-8 text-center text-uf-text-muted">No tasks for today</div>}
          </div>
        </div>
      )}

      {view === 'all' && (
        <div className="space-y-2">
          {tasks.map(t => <TaskCard key={t.id} task={t} onComplete={completeTask} priorityColors={priorityColorsMap} typeIcons={typeIconsMap} />)}
          {!tasks.length && <div className="bg-uf-surface rounded-xl border border-uf-border p-8 text-center text-uf-text-muted">No tasks</div>}
        </div>
      )}

      {showAdd && (
        <Modal title="New Task" onClose={() => setShowAdd(false)}>
          <div className="space-y-3">
            <input data-testid="task-subject" placeholder="Subject" value={newTask.subject} onChange={e => setNewTask({...newTask, subject: e.target.value})} className="w-full bg-uf-surface-hi border border-uf-border rounded-lg px-3 py-2 text-uf-text text-sm" />
            <textarea placeholder="Description" value={newTask.description} onChange={e => setNewTask({...newTask, description: e.target.value})} className="w-full bg-uf-surface-hi border border-uf-border rounded-lg px-3 py-2 text-uf-text text-sm h-16 resize-none" />
            <div className="grid grid-cols-2 gap-3">
              <select value={newTask.task_type} onChange={e => setNewTask({...newTask, task_type: e.target.value})} style={{ width: '100%' }} className="bg-uf-surface-hi border border-uf-border rounded-lg px-3 py-2 text-uf-text text-sm">
                {['call','email','follow_up','tour','general'].map(t => <option key={t} value={t}>{t.replace('_',' ')}</option>)}
              </select>
              <select value={newTask.priority} onChange={e => setNewTask({...newTask, priority: e.target.value})} style={{ width: '100%' }} className="bg-uf-surface-hi border border-uf-border rounded-lg px-3 py-2 text-uf-text text-sm">
                {['low','medium','high','urgent'].map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <input type="date" value={newTask.due_date} onChange={e => setNewTask({...newTask, due_date: e.target.value})} className="bg-uf-surface-hi border border-uf-border rounded-lg px-3 py-2 text-uf-text text-sm" />
              <input type="time" value={newTask.due_time} onChange={e => setNewTask({...newTask, due_time: e.target.value})} className="bg-uf-surface-hi border border-uf-border rounded-lg px-3 py-2 text-uf-text text-sm" />
            </div>
            <div className="flex gap-3 pt-2">
              <button onClick={() => setShowAdd(false)} className="flex-1 border border-uf-border text-uf-text py-2 rounded-lg text-sm">Cancel</button>
              <button data-testid="save-task-btn" onClick={createTask} className="flex-1 bg-uf-red text-white py-2 rounded-lg text-sm">Save</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

const TaskCard = ({ task, onComplete, priorityColors, typeIcons }) => {
  const Icon = typeIcons[task.task_type] || Target;
  return (
    <div className="bg-uf-surface rounded-lg border border-uf-border p-3 flex items-center gap-3 mb-2" data-testid={`task-${task.id}`}>
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${task.status === 'completed' ? 'bg-emerald-500/20' : 'bg-uf-surface-hi'}`}>
        {task.status === 'completed' ? <Check className="w-4 h-4 text-emerald-400" /> : <Icon className="w-4 h-4 text-uf-text-muted" />}
      </div>
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-medium ${task.status === 'completed' ? 'text-uf-text-muted line-through' : 'text-uf-text'}`}>{task.subject}</p>
        <div className="flex items-center gap-2 text-xs text-uf-text-muted">
          <span className={priorityColors[task.priority]}>{task.priority}</span>
          {task.due_time && <span>{task.due_time}</span>}
          {task.due_date && <span>{task.due_date}</span>}
          {task.related_name && <span>- {task.related_name}</span>}
        </div>
      </div>
      {task.status !== 'completed' && onComplete && (
        <button onClick={() => onComplete(task.id)} className="p-1.5 bg-emerald-500/10 text-emerald-400 rounded hover:bg-emerald-500/20 flex-shrink-0"><Check className="w-4 h-4" /></button>
      )}
    </div>
  );
};

// ==================== AUTOMATIONS (ENHANCED) ====================
const TRIGGER_TYPES = {
  web_dropoff: { label: 'Web Drop-Off', icon: AlertTriangle, color: 'bg-amber-500/20 text-amber-400', desc: 'When someone abandons the sign-up form' },
  new_member: { label: 'New Member Joined', icon: UserPlus, color: 'bg-emerald-500/20 text-emerald-400', desc: 'When a new membership is activated' },
  payment_failed: { label: 'Payment Failed', icon: CreditCard, color: 'bg-red-500/20 text-red-400', desc: 'When a payment collection fails' },
  cancellation_risk: { label: 'Cancellation Risk', icon: AlertTriangle, color: 'bg-orange-500/20 text-orange-400', desc: 'When a member shows cancellation signals' },
  lead_created: { label: 'New Lead Created', icon: Target, color: 'bg-blue-500/20 text-blue-400', desc: 'When a new lead enters the pipeline' },
  day_pass_purchased: { label: 'Day Pass Purchased', icon: Ticket, color: 'bg-violet-500/20 text-violet-400', desc: 'When someone buys a day pass' },
  stage_change: { label: 'Lead Stage Changed', icon: ArrowRight, color: 'bg-cyan-500/20 text-cyan-400', desc: 'When a lead moves to a specific pipeline stage' },
  inactivity: { label: 'Lead Inactive', icon: TimerOff, color: 'bg-gray-500/20 text-gray-400', desc: 'When a lead has had no activity for X days' },
  tour_completed: { label: 'Tour Completed', icon: Building2, color: 'bg-indigo-500/20 text-indigo-400', desc: 'When a lead completes their gym tour' },
  contact_form: { label: 'Contact Form Submitted', icon: Inbox, color: 'bg-teal-500/20 text-teal-400', desc: 'When someone submits the website contact form' },
};

const AutomationsPanel = ({ headers }) => {
  const navigate = useNavigate();
  const [journeys, setJourneys] = useState([]);

  const fetchJourneys = useCallback(async () => {
    const res = await fetch(`${API_URL}/api/crm/automations`, { headers });
    setJourneys(await res.json());
  }, []);

  useEffect(() => { fetchJourneys(); }, [fetchJourneys]);

  const toggleActive = async (id, isActive) => {
    await fetch(`${API_URL}/api/crm/automations/${id}`, { method: 'PATCH', headers, body: JSON.stringify({ is_active: !isActive }) });
    fetchJourneys();
  };

  const deleteJourney = async (id) => {
    if (!window.confirm('Are you sure you want to delete this automation journey?')) return;
    await fetch(`${API_URL}/api/crm/automations/${id}`, { method: 'DELETE', headers });
    fetchJourneys();
  };

  return (
    <div data-testid="automations-panel">
      <div className="flex justify-between items-center mb-6">
        <div>
          <p className="text-uf-text-muted text-sm">Automated journeys triggered by system events</p>
        </div>
        <button data-testid="open-builder-new" onClick={() => navigate('/admin/automations/new')} className="flex items-center gap-2 bg-uf-red text-white px-4 py-2 rounded-lg text-sm"><Zap className="w-4 h-4" />New Journey</button>
      </div>

      {/* Journey List */}
      <div className="space-y-3">
        {journeys.map(j => {
          const trigger = TRIGGER_TYPES[j.trigger_type] || { label: j.trigger_type, color: 'bg-gray-500/20 text-gray-400', icon: Zap };
          const TriggerIcon = trigger.icon;
          const nodeCount = (j.workflow_nodes || []).length;
          const emailCount = (j.workflow_nodes || []).filter(n => n.type === 'emailNode').length;
          const hasDesign = (j.workflow_nodes || []).some(n => n.data?.emailBlocks?.length > 0);
          return (
            <div key={j.id} className="bg-uf-surface rounded-xl border border-uf-border p-5 hover:border-uf-text-muted/30 transition-colors cursor-pointer" 
              onClick={() => navigate(`/admin/automations/${j.id}`)} data-testid={`journey-${j.id}`}>
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-uf-text font-semibold">{j.name}</h3>
                  <div className="flex items-center gap-2 mt-1.5">
                    <span className={`px-2 py-0.5 rounded text-xs flex items-center gap-1 ${trigger.color}`}>
                      <TriggerIcon className="w-3 h-3" />{trigger.label}
                    </span>
                    {nodeCount > 0 && <span className="text-uf-text-muted text-xs">{nodeCount} nodes</span>}
                    {emailCount > 0 && <><span className="text-uf-text-muted text-xs">|</span><span className="text-uf-text-muted text-xs">{emailCount} emails</span></>}
                    {hasDesign && <span className="text-emerald-400 text-[10px] bg-emerald-500/10 px-1.5 py-0.5 rounded">Designed</span>}
                    <span className="text-uf-text-muted text-xs">|</span>
                    <span className="text-blue-400 text-xs">{j.enrolled_count || 0} active</span>
                    <span className="text-emerald-400 text-xs">{j.completed_count || 0} completed</span>
                  </div>
                </div>
                <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                  <button onClick={() => deleteJourney(j.id)} className="px-3 py-1 rounded-lg text-xs font-medium bg-red-500/20 text-red-400 hover:bg-red-500/30" data-testid={`delete-journey-${j.id}`}>
                    Delete
                  </button>
                  <button onClick={() => toggleActive(j.id, j.is_active)} className={`px-3 py-1 rounded-lg text-xs font-medium flex items-center gap-1.5 ${j.is_active ? 'bg-emerald-500/20 text-emerald-400' : 'bg-gray-500/20 text-gray-400'}`}>
                    {j.is_active ? <><Play className="w-3 h-3" />Active</> : <><Pause className="w-3 h-3" />Paused</>}
                  </button>
                </div>
              </div>
            </div>
          );
        })}
        {!journeys.length && (
          <div className="bg-uf-surface rounded-xl border border-uf-border border-dashed p-12 text-center">
            <Zap className="w-8 h-8 text-uf-text-muted mx-auto mb-3 opacity-50" />
            <p className="text-uf-text font-medium mb-1">No automation journeys yet</p>
            <p className="text-uf-text-muted text-sm mb-4">Create your first journey to start automating lead engagement</p>
            <button onClick={() => navigate('/admin/automations/new')} className="bg-uf-red text-white px-4 py-2 rounded-lg text-sm inline-flex items-center gap-2">
              <Plus className="w-4 h-4" />Create First Journey
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// ==================== SHARED ====================
const Modal = ({ title, onClose, children }) => (
  <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" onClick={onClose}>
    <div className="bg-uf-surface rounded-xl border border-uf-border p-6 w-full max-w-lg mx-4" onClick={e => e.stopPropagation()}>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-uf-text">{title}</h3>
        <button onClick={onClose} className="text-uf-text-muted hover:text-uf-text"><X className="w-5 h-5" /></button>
      </div>
      {children}
    </div>
  </div>
);

export default CRMHub;
